import React from 'react'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import daLocale from 'dayjs/locale/da'
import max from 'lodash/max'

import StatusBadge from '../StatusBadge'
import PillBadge from '../PillBadge'

import { formatQuantity } from '../helpers/numbers'
import { formatDate } from '../helpers/dates'
import { getMeterTypeLabel } from '../helpers/meterTypes'
import ALLOWED_STATUSES from '../config/allowedStatuses'

dayjs.locale(daLocale)
dayjs.extend(relativeTime)

// We do not wanto to show electricity power currently
const filterMeasurements = R.reject(R.propEq('meterType', 'electricity_power'))

/* Sub components ----------------------------------------------------------- */
const Measurement = ({ meterType, value }) => {
  const meterTypeLabel = getMeterTypeLabel(meterType)

  return (
    <tr data-testid="measurement">
      <td className="pt1">{`${meterTypeLabel}:`}</td>
      <td className="pt1 tr">{formatQuantity(value)}</td>
    </tr>
  )
}

/* Main component ----------------------------------------------------------- */
export default function DeviceReport({
  roomName = 'Ukendt rum',
  measurements = [],
  displayNumber,
  hoverText,
  deviceStatus = 'not-available',
  className,
}) {
  const filteredMeasurements = filterMeasurements(measurements)

  const mostRecentMeasurementAt = max(
    filteredMeasurements.map(({ measurementAt }) => measurementAt)
  )

  return (
    <div className={className}>
      <div className="relative">
        <StatusBadge
          status={deviceStatus}
          className="absolute top-0 left-0 mt1"
          title={deviceStatus === 'ok' ? 'Online' : 'Offline'}
        />
        <div className="pl4">
          <div className="truncate">
            <span className="f6 lh-copy">{roomName}</span>
            {mostRecentMeasurementAt && (
              <PillBadge
                className="f8 moon-gray tc nowrap ml2 mt1 fr"
                title={formatDate(mostRecentMeasurementAt, 'DD.MM.YYYY HH:mm')}
              >
                {dayjs(mostRecentMeasurementAt).fromNow()}
              </PillBadge>
            )}
          </div>

          <table className="f7 moon-gray w-100 mv1 collapse">
            <tbody>
              {filteredMeasurements
                .sort((a, b) =>
                  getMeterTypeLabel(a.meterType).localeCompare(
                    getMeterTypeLabel(b.meterType)
                  )
                )
                .map(({ id, meterType, value }) => (
                  <Measurement key={id} meterType={meterType} value={value} />
                ))}
            </tbody>
          </table>
          <div
            className="silver hover-moon-gray f8 help pt1"
            title={hoverText}
          >{`# ${displayNumber}`}</div>
        </div>
      </div>
    </div>
  )
}

DeviceReport.propTypes = {
  roomName: PropTypes.string,
  displayNumber: PropTypes.string,
  hoverText: PropTypes.string,
  deviceStatus: PropTypes.oneOf(ALLOWED_STATUSES),
  className: PropTypes.string,
}
